
import React from 'react';
import { useRoleDashboardStats } from '@/hooks/use-role-dashboard-stats';
import { usePendingDiscrepancyStats } from '@/hooks/use-payment-discrepancies';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, Clock, TrendingUp, DollarSign, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';

const FinanceDashboard: React.FC = () => {
  const stats = useRoleDashboardStats('finance');
  const { data: discrepancyStats } = usePendingDiscrepancyStats();
  const navigate = useNavigate();

  if (stats.isLoading) {
    return (
      <div className="p-6">Loading finance dashboard...</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Finance Dashboard</h1>
        <p className="text-muted-foreground mt-1">Procurement approvals and financial overview</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Approvals</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.pendingApprovals || 0}</div>
            <p className="text-xs text-muted-foreground">Awaiting your review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved This Month</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.approvedThisMonth || 0}</div>
            <p className="text-xs text-muted-foreground">Procurements approved</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Br{stats.totalValue?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">All-time procurement value</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Br{stats.monthlyValue?.toLocaleString() || 0}</div>
            <p className="text-xs text-muted-foreground">This month's total</p>
          </CardContent>
        </Card>
      </div>

      {/* Discrepancy Alert */}
      {discrepancyStats && discrepancyStats.count > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex items-center gap-4 p-4">
            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-amber-900">
                {discrepancyStats.count} Payment {discrepancyStats.count === 1 ? 'Discrepancy Requires' : 'Discrepancies Require'} Attention
              </p>
              <p className="text-sm text-amber-700">
                Total variance: {formatCurrency(discrepancyStats.totalAmount)}
              </p>
            </div>
            <Button
              onClick={() => navigate('/finance/payments/discrepancies')}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Review Discrepancies
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Action Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Pending Approvals</CardTitle>
            <Button onClick={() => navigate('/finance')}>View All</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.pendingProcurements?.slice(0, 5).map((procurement: any) => (
                <div key={procurement.id} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded">
                  <div>
                    <p className="font-medium">{procurement.product_name || 'N/A'}</p>
                    <p className="text-sm text-muted-foreground">
                      Br{procurement.total_amount?.toLocaleString() || 0} • {procurement.total_quantity || 0} kg
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/finance/approval/${procurement.id}`)}
                  >
                    Review
                  </Button>
                </div>
              )) || <p className="text-muted-foreground">No pending approvals</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recently Approved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recentApprovals?.map((procurement: any) => (
                <div key={procurement.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{procurement.product_name || 'N/A'}</p>
                    <p className="text-sm text-muted-foreground">
                      Br{procurement.total_amount?.toLocaleString() || 0} • {procurement.total_quantity || 0} kg
                    </p>
                  </div>
                  <Badge variant="default">Approved</Badge>
                </div>
              )) || <p className="text-muted-foreground">No recent approvals</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinanceDashboard;
